import Head from "next/head";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import constants from "@/global/config/constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect to your Brooklyn Nets account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for the games.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Brooklyn Nets{" "}
        <Link
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
          href="https://am.ticketmaster.com/bse/"
        >
          Account Manager
        </Link>{" "}
        within 24 hours of purchase. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How do the Brooklyn Nets Flex Plans work?",
    description:
      "The Brooklyn Nets Flex Plan lets you fully customize your experience. You choose any home games that best fit your schedule and select any available seats for each game—giving you flexibility and control over your season.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description:
      "Event tickets are delivered directly within your Brooklyn Nets Account Manager.",
  },
];

export const RegistrationTerms = () => (
  <>
    By creating an account you agree to receive email notifications regarding
    your purchase and to the{" "}
    <Link
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      target={"_blank"}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const CheckoutTerms = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1708698089343398');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1708698089343398&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
